import type { FacetProps } from '@iheartjane/algolia';
import { fetchFacets } from '@iheartjane/algolia';
import { config } from '@iheartjane/config';
import { useQuery } from '@tanstack/react-query';
import type { ZodObject, ZodRawShape } from 'zod';

interface FacetQueryProps<T extends ZodRawShape> extends FacetProps {
  enabled?: boolean;
  schemaObject?: ZodObject<T>;
}

const facetQueryBody = <T extends ZodRawShape>(params: FacetQueryProps<T>) => ({
  queryFn: () => fetchAlgoliaFacets(params),
  queryKey: ['facets', JSON.stringify(params)],
  enabled: params.enabled,
});

export function useFacets<T extends ZodRawShape>(params: FacetQueryProps<T>) {
  return useQuery(facetQueryBody(params));
}

export function useGoldFacets<T extends ZodRawShape>(
  params: FacetQueryProps<T>
) {
  const { data: facets, isLoading } = useQuery(facetQueryBody(params));

  let finalFacets = facets;
  if (finalFacets && !finalFacets['has_brand_discount']) {
    finalFacets = { has_brand_discount: { true: 0 } };
  }

  return { data: finalFacets, isLoading };
}

async function fetchAlgoliaFacets<T extends ZodRawShape>({
  enabled: _enabled,
  schemaObject,
  ...args
}: FacetQueryProps<T>) {
  const facets = await fetchFacets(
    {
      algoliaEnv: config.algoliaEnv,
      apiKey: config.algoliaApiKey,
      appId: config.algoliaAppId,
      url: config.algoliaUrl,
    },
    args
  );

  return schemaObject ? await schemaObject.parseAsync(facets) : facets;
}
